import { call, put, all, takeLatest } from 'redux-saga/effects';
import { IGenericDocumentModifier } from '../../types/global/helper';
import {
    IGetCompanyDetails,
    ICreateCompany,
    IUpdateCompany,
    IAddCompanyReview,
    IEditCompanyReview,
    ILikeCompanyReview,
    IDeleteCompanyReview,
    ICreatePosition,
    IApprovePosition,
    IDeletePosition,
} from './types';
import {
    addCompanyReview,
    addCompanyReviewFail,
    addCompanyReviewSuccess,
    createCompany,
    createCompanyFail,
    createCompanySuccess,
    deleteCompany,
    deleteCompanyFail,
    deleteCompanyReview,
    deleteCompanyReviewFail,
    deleteCompanyReviewSuccess,
    deleteCompanyReviewLike,
    deleteCompanyReviewLikeFail,
    deleteCompanyReviewLikeSuccess,
    deleteCompanySuccess,
    editCompanyReview,
    editCompanyReviewFail,
    editCompanyReviewSuccess,
    getCompanyDetails,
    getCompanyDetailsFail,
    getCompanyDetailsSuccess,
    likeCompanyReview,
    likeCompanyReviewFail,
    likeCompanyReviewSuccess,
    searchCompanies,
    searchCompaniesSuccess,
    searchCompaniesFail,
    updateCompany,
    updateCompanyFail,
    updateCompanySuccess,
    createPosition,
    createPositionSuccess,
    createPositionFail,
    approvePosition,
    approvePositionSuccess,
    approvePositionFail,
    deletePosition,
    deletePositionSuccess,
    deletePositionFail,
} from './actions';
import { api } from '../../configurations/api';
import { companyDetailsUrl, companyReviewsUrl, companyUrl, likeUrl, positionsUrl } from '../../configurations/api/url';
import { httpRequest } from '../types';
import { globalErrorHandler } from '../error/saga';
import { SentryCapture } from '../../analytics/Sentry';
import { showToastMessage } from '../../utils/AppUtils';
import { uploadFilesSaga } from '../upload/saga';

function* getCompanyDetailsSaga({ payload }: { payload: IGetCompanyDetails }): any {
    try {
        // Build query parameters from payload
        const queryParams = new URLSearchParams();

        // Add pagination parameters
        if (payload.reviewsPage) queryParams.append('reviewsPage', String(payload.reviewsPage));
        if (payload.reviewsLimit) queryParams.append('reviewsLimit', String(payload.reviewsLimit));
        if (payload.auditionsPage) queryParams.append('auditionsPage', String(payload.auditionsPage));
        if (payload.auditionsLimit) queryParams.append('auditionsLimit', String(payload.auditionsLimit));
        if (payload.jobsPage) queryParams.append('jobsPage', String(payload.jobsPage));
        if (payload.jobsLimit) queryParams.append('jobsLimit', String(payload.jobsLimit));
        if (payload.campaignsPage) queryParams.append('campaignsPage', String(payload.campaignsPage));
        if (payload.campaignsLimit) queryParams.append('campaignsLimit', String(payload.campaignsLimit));

        // Add with flags
        if (payload.withReviews !== undefined) queryParams.append('withReviews', String(payload.withReviews));
        if (payload.withAuditions !== undefined) queryParams.append('withAuditions', String(payload.withAuditions));
        if (payload.withJobs !== undefined) queryParams.append('withJobs', String(payload.withJobs));
        if (payload.withCampaigns !== undefined) queryParams.append('withCampaigns', String(payload.withCampaigns));
        if (payload.withLeadership !== undefined) queryParams.append('withLeadership', String(payload.withLeadership));
        if (payload.withSimilarCompanies !== undefined)
            queryParams.append('withSimilarCompanies', String(payload.withSimilarCompanies));

        // Add singleFetch flag
        if (payload.singleFetch !== undefined) queryParams.append('singleFetch', String(payload.singleFetch));

        // Add review sorting parameters
        if (payload.reviewsSortBy) queryParams.append('reviewsSortBy', payload.reviewsSortBy);
        if (payload.reviewsSortOrder) queryParams.append('reviewsSortOrder', payload.reviewsSortOrder);

        const queryString = queryParams.toString();
        const url = queryString
            ? `${companyDetailsUrl}/${payload.slug}?${queryString}`
            : `${companyDetailsUrl}/${payload.slug}`;

        const response = yield call(api, url, httpRequest.GET, null, 0, 0);
        const { data } = response;

        // Pass data with singleFetch flag
        yield put(getCompanyDetailsSuccess({ data, singleFetch: payload.singleFetch }));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(getCompanyDetailsFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* createCompanySaga({ payload }: { payload: ICreateCompany }): any {
    try {
        // Replace with actual create company endpoint if different
        const response = yield call(api, `${companyDetailsUrl}`, httpRequest.POST, payload, 0, 0);
        const { data } = response.data;
        yield put(createCompanySuccess(data));
        yield call(showToastMessage, 'Company created successfully', 'success');
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(createCompanyFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* updateCompanySaga({ payload }: { payload: IUpdateCompany }): any {
    try {
        const { id, body } = payload;
        let updateBody = { ...body };
        let profilePictureUpload;

        // Handle profile picture upload if present
        if (body.profile_picture && Array.isArray(body.profile_picture) && body.profile_picture[0] instanceof File) {
            profilePictureUpload = yield call(uploadFilesSaga, {
                payload: {
                    collection: 'company',
                    directory: 'media',
                    path: 'images',
                    files: body.profile_picture,
                    id: id,
                },
            });
            updateBody.profile_picture = profilePictureUpload[0];
        }

        // Handle gallery uploads if present
        if (body.gallery && Array.isArray(body.gallery) && body.gallery.length > 0) {
            const galleryUpload = yield call(uploadFilesSaga, {
                payload: {
                    collection: 'company',
                    directory: 'media',
                    path: 'gallery',
                    files: body.gallery,
                    id: id,
                },
            });
            updateBody.gallery = galleryUpload;
        }

        const response = yield call(api, `${companyUrl}/${id}`, httpRequest.PATCH, updateBody, 0, 0);
        const { data } = response.data;
        yield put(updateCompanySuccess(data));
        yield call(showToastMessage, 'Company updated successfully', 'success');
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(updateCompanyFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* deleteCompanySaga({ payload }: { payload: string }): any {
    try {
        yield call(api, `${companyUrl}/${payload}`, httpRequest.DELETE, null, 0, 0);
        yield put(deleteCompanySuccess(payload));
        yield call(showToastMessage, 'Company deleted successfully', 'success');
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(deleteCompanyFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* addCompanyReviewSaga({ payload }: { payload: IAddCompanyReview }): any {
    try {
        const response = yield call(api, `${companyReviewsUrl}`, httpRequest.POST, payload, 0, 0);
        const { data } = response.data;
        yield put(addCompanyReviewSuccess(data));
        yield call(showToastMessage, 'Review added successfully', 'success');
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(addCompanyReviewFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* editCompanyReviewSaga({ payload }: { payload: IEditCompanyReview }): any {
    try {
        const response = yield call(api, `${companyReviewsUrl}/${payload.reviewId}`, httpRequest.PATCH, payload, 0, 0);
        const { data } = response.data;
        yield put(editCompanyReviewSuccess(data));
        yield call(showToastMessage, 'Review updated successfully', 'success');
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(editCompanyReviewFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* likeCompanyReviewSaga({ payload }: { payload: ILikeCompanyReview }): any {
    const { like_type, reviewId, cache, total } = payload;

    // Construct query params
    const queryParams = new URLSearchParams();
    if (cache !== undefined) queryParams.append('cache', cache.toString());
    if (total !== undefined) queryParams.append('total', total.toString());

    const url = `${companyReviewsUrl}/${reviewId}/like?${queryParams.toString()}`;

    try {
        const response = yield call(api, url, httpRequest.POST, { like_type }, 0, 0);
        const { data } = response.data;
        yield put(likeCompanyReviewSuccess(data));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(likeCompanyReviewFail({ ...error, reviewId, like_type }));
        yield call(globalErrorHandler, error);
    }
}

function* deleteCompanyReviewSaga({ payload }: { payload: IDeleteCompanyReview }): any {
    try {
        yield call(api, `${companyDetailsUrl}/review/${payload.reviewId}`, httpRequest.DELETE, null, 0, 0);
        yield put(deleteCompanyReviewSuccess(payload.reviewId));
        yield call(showToastMessage, 'Review deleted successfully', 'success');
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(deleteCompanyReviewFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* deleteCompanyReviewLikeSaga({ payload }: { payload: IGenericDocumentModifier }): any {
    try {
        yield call(api, `${likeUrl}/${payload.documentId}`, httpRequest.DELETE, {}, 0, 0);
        yield put(deleteCompanyReviewLikeSuccess(payload));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(
            deleteCompanyReviewLikeFail({
                ...error,
                additionalPayload: payload.additionalPayload,
                morePayload: payload.morePayload,
            }),
        );
        yield call(globalErrorHandler, error);
    }
}

function* searchCompaniesSaga({ payload }: { payload: { searchTerm: string } }): any {
    try {
        const response = yield call(api, `${companyUrl}/general/search`, httpRequest.POST, payload, 0, 0);
        const { data } = response;
        yield put(searchCompaniesSuccess(data));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(searchCompaniesFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* createPositionSaga({ payload }: { payload: ICreatePosition }): any {
    try {
        const response = yield call(api, positionsUrl, httpRequest.POST, payload, 0, 0);
        const { data } = response.data;
        yield put(createPositionSuccess(data));
        yield call(showToastMessage, 'Position created successfully', 'success');
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(createPositionFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* approvePositionSaga({ payload }: { payload: IApprovePosition }): any {
    try {
        const response = yield call(api, `${positionsUrl}/${payload.positionId}/approve`, httpRequest.PATCH, {}, 0, 0);
        const { data } = response;
        yield put(approvePositionSuccess(data));
        yield call(showToastMessage, 'Position approved successfully', 'success');
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(approvePositionFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* deletePositionSaga({ payload }: { payload: IDeletePosition }): any {
    try {
        yield call(api, `${positionsUrl}/${payload.positionId}`, httpRequest.DELETE, {}, 0, 0);
        yield put(deletePositionSuccess(payload));
        yield call(showToastMessage, 'Position deleted successfully', 'success');
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(deletePositionFail(error));
        yield call(globalErrorHandler, error);
    }
}

export default function* companySaga() {
    yield all([
        takeLatest(getCompanyDetails, getCompanyDetailsSaga),
        takeLatest(createCompany, createCompanySaga),
        takeLatest(updateCompany, updateCompanySaga),
        takeLatest(deleteCompany, deleteCompanySaga),
        takeLatest(searchCompanies, searchCompaniesSaga),
        takeLatest(addCompanyReview, addCompanyReviewSaga),
        takeLatest(editCompanyReview, editCompanyReviewSaga),
        takeLatest(likeCompanyReview, likeCompanyReviewSaga),
        takeLatest(deleteCompanyReview, deleteCompanyReviewSaga),
        takeLatest(deleteCompanyReviewLike, deleteCompanyReviewLikeSaga),
        takeLatest(createPosition, createPositionSaga),
        takeLatest(approvePosition, approvePositionSaga),
        takeLatest(deletePosition, deletePositionSaga),
    ]);
}
